import { useEffect, useRef, useState } from "react";
import { useAccountsStore } from "./store";
import { TradingAccount, TradingAccountInput } from "./types";
import { useBrokersStore } from "@/lib/brokers/store";
import { useConnectionProgressStore } from "@/vybe-trader-client/connection-progress";
import { useSymbolsStore } from "@/vybe-trader-client/symbols";
import { useTradeHistoryStore } from "@/vybe-trader-client/trade-history/stores/useTradeHistoryStore";
import { generateAccountKey } from "@/vybe-trader-client/chart/utils/accountHelpers";
import { useVybeTraderClient } from "@/vybe-trader-client/connection";
import { TradingProvider } from "@/vybe-trader-client/connection/config/signalr";
import { useJournalStore } from "@/vybe-trader-client/messaging/stores/useJournalStore";
import { prepareAndSyncMailbox } from "@/vybe-trader-client/mailbox/syncMailbox";
import { getVybeToken, useVybeAuthStore } from "@/lib/vybe-auth/store";
import { useSyncedAccountsStore, refreshVybeCredential } from "./syncedAccounts";
import { prepareAndSyncNews } from "@/vybe-trader-client/news/syncNews";
import { useMailboxStore } from "@/vybe-trader-client/mailbox/stores/useMailboxStore";
import { formatTradingAccountKey } from "@/vybe-trader-client/chart/utils/accountHelpers";
import { playNotificationSound } from "@/lib/sounds";
import { isKnownGatewayAddress } from "@/lib/gateways/addresses";
import {
  isAuthFailure,
  isCriticalConnectionError,
  showConnectionError,
} from "./connectionErrors";
import { isHubConnectionClosedError } from "@/vybe-trader-client/connection/utils/hubErrors";
import {
  VYBE_BRAND_LOGO_DATA_URI,
  isVybeGatewaySlug,
  normalizeBrokerLogoUrl,
} from "./brokerLogo";

/**
 * Automatic re-dial schedule after a TRANSIENT connect failure (offline at boot, gateway restarting,
 * the previous page's session still held by the gateway for a few seconds after a PWA refresh).
 * The very first connect has no SignalR auto-reconnect behind it — `withAutomaticReconnect` only
 * covers drops after a successful start — so before this the terminal made exactly one attempt per
 * server and then sat on a red dot until the app was relaunched (SR Global iPhone PWA, 2026-09-08).
 * Finite: after the last slot the "Connection Issue" modal is shown as before; the wake watchdog
 * and the Settings card's Reconnect control remain available after that.
 */
const AUTO_RECONNECT_DELAYS_MS = [3000, 5000, 10000, 20000, 30000];

/**
 * Initialize accounts store on mount
 * Should be called once in the app root
 */
/**
 * Bind the internal account sync to the session, once per page life.
 *
 * A session ARRIVING fetches the profile's accounts; a session ENDING drops them and purges the rows it
 * materialised. Only a real transition to signed-out purges: at boot the auth store has not hydrated yet, so
 * treating that initial null as a sign-out would delete a returning user's synced rows on every reload,
 * before their session had a chance to come back.
 */
let accountSyncSubscribed = false;
const subscribeAccountSync = () => {
  if (accountSyncSubscribed) return;
  accountSyncSubscribed = true;

  const signedOut = () => {
    useSyncedAccountsStore.getState().clear();
    useAccountsStore.getState().purgeSyncedAccounts();
  };

  if (useVybeAuthStore.getState().token) void useSyncedAccountsStore.getState().load();

  useVybeAuthStore.subscribe((state, previous) => {
    if (state.token === previous.token) return;
    if (state.token) void useSyncedAccountsStore.getState().load();
    else if (previous.token) signedOut();
  });

  // Focus backstop for the realtime presence push: changes made while this window was hidden — or
  // made directly on the engine, which never pass through the CRM's notifier — land on the next
  // focus. Throttled so alt-tabbing doesn't hammer the CRM.
  if (typeof window !== "undefined") {
    let lastFocusRefresh = 0;
    window.addEventListener("focus", () => {
      if (!useVybeAuthStore.getState().token) return;
      const now = Date.now();
      if (now - lastFocusRefresh < 30_000) return;
      lastFocusRefresh = now;
      void useSyncedAccountsStore.getState().refresh();
    });
  }
};

export const initializeAccountsStore = () => {
  const loadAccounts = useAccountsStore.getState().loadAccounts;
  loadAccounts();
  subscribeAccountSync();
  // Strictly sequential: the logo backfill rewrites companyName/serverName from whichever gateway
  // answers first, so letting it race the hub repair lets it re-label an account against a foreign
  // broker's catalog (observed live: "Vybe Trader Live" was rewritten to "VybeTrader-Demo"), which
  // then defeats the repair's own server-name matching on the next boot.
  void repairUnreachableHubUrls().then(() => backfillBrokerLogos());
};

/**
 * Is this hub URL unusable from the page we are running on?
 *
 * A connect deep-link bakes the gateway's hub URL into the stored account, so a CRM that was
 * misconfigured when the link was minted poisons that account FOREVER — the terminal keeps dialling
 * the bad URL on every future boot, long after the CRM is fixed. That happened in production: the
 * CRM's `Crm:Connect:HubUrl` briefly carried the compose-internal `http://signalr-vybe:5031`, which
 * a browser on https:// refuses outright ("An insecure WebSocket connection may not be initiated
 * from a page loaded over HTTPS") — the account simply never connects again.
 *
 * Two unusable shapes:
 *  - a host with no dot (a compose service name) or an explicit loopback — not resolvable off-box;
 *  - a plain-http hub while the page itself is https — blocked as mixed content.
 * Anything else (including http on an http dev page) is left alone.
 */
/**
 * The discovered gateway list, waiting for discovery if it has not finished.
 *
 * `initializeGatewaysStore()` fires at app start and leaves `isLoading` true for the first moment of
 * the boot, so a "load only if empty AND not already loading" guard silently reads an empty list and
 * gives up — the exact race that made the repair below a no-op on the first run. Re-issuing the
 * fetch is cheap and, unlike the guard, cannot lose to the in-flight one.
 */
const ensureGatewaysLoaded = async () => {
  const { useGatewaysStore } = await import("@/lib/gateways/store");
  const existing = useGatewaysStore.getState().gateways;
  if (existing.length > 0) return existing;
  await useGatewaysStore.getState().load();
  return useGatewaysStore.getState().gateways;
};

/** Gateway hosts that no longer exist; an account stored against one must be re-homed. */
const RETIRED_HUB_HOSTS = new Set(["vii.gw.vybetrader.com"]);

/**
 * Server names a retired gateway advertised that the broker's own gateway now advertises under a
 * different name → the slug of the gateway that serves it today. Only needed where the name
 * changed; every other broker kept its server name and matches on it directly.
 */
const RETIRED_SERVER_HOMES: Record<string, string> = {
  "VybeTrader-Demo": "sig-vybe",
};

export const isHubUrlUnreachable = (hubUrl: string): boolean => {
  try {
    const u = new URL(
      hubUrl.replace(/^wss:\/\//i, "https://").replace(/^ws:\/\//i, "http://")
    );
    if (!u.hostname.includes(".")) return true;
    if (u.hostname === "localhost" || u.hostname === "127.0.0.1") return true;
    // Gateways retired with the hftteam.com → vybetrader.com move; the DNS may still
    // resolve, but nothing answers there and a connect hangs at SendLogin forever.
    if (u.hostname.endsWith(".vybetrader.hftteam.com")) return true;
    // The shared ViiSync gateway was retired 2026-10-08: every ViiSync broker now runs its own
    // gateway. Accounts saved against it are re-pointed by runHubRepair (matched on the server
    // name, which each broker's own gateway advertises unchanged).
    if (RETIRED_HUB_HOSTS.has(u.hostname.toLowerCase())) return true;
    // A plaintext hub is unreachable from any page that is not itself plain http.
    // Checking for "not http:" rather than "is https:" matters on the desktop shell,
    // where the page protocol is tauri: — the old check left this branch dead there,
    // so desktop never repaired http hubs the way the web terminal does.
    return (
      u.protocol === "http:" &&
      typeof window !== "undefined" &&
      window.location.protocol !== "http:"
    );
  } catch {
    return false;
  }
};

/**
 * One-shot repair for accounts whose stored `hubUrl` can never connect from here. The CRM's
 * `GET /api/gateways` is authoritative for where a gateway actually lives, so re-point the account
 * at the gateway that advertises its server — matching on the stored slug first, then on the server
 * name, then on the broker endpoint. Also refreshes a drifted `gatewaySlug` while we hold the match.
 *
 * Deliberately conservative: an account whose hub is merely *down* is untouched (only structurally
 * unreachable URLs qualify), and an account we cannot confidently match is left as-is rather than
 * guessed at.
 */
let hubRepairInFlight: Promise<void> | null = null;
export const repairUnreachableHubUrls = (): Promise<void> => {
  // Share the in-flight run instead of returning early: the connect path awaits this to
  // know the repair has finished, so a bare re-entrancy guard would let it dial too soon.
  hubRepairInFlight ??= runHubRepair().finally(() => {
    hubRepairInFlight = null;
  });
  return hubRepairInFlight;
};

const runHubRepair = async () => {
  try {
    const { accounts } = useAccountsStore.getState();
    // A missing hubUrl is as broken as an unreachable one: connect falls back to the
    // platform's baked default hub, which on desktop is the MT5-pinned gateway (or
    // localhost in a dev build) and hangs with no error for anything else.
    const broken = accounts.filter(
      (a) =>
        !a.gatewaySlug?.startsWith("custom:") &&
        (!a.hubUrl || isHubUrlUnreachable(a.hubUrl))
    );
    if (broken.length === 0) return;

    const advertised = await ensureGatewaysLoaded();
    const gateways = advertised.filter(
      (g) => g.hubUrl && !isHubUrlUnreachable(g.hubUrl)
    );
    if (gateways.length === 0) return;

    for (const account of broken) {
      // A broker may run several gateways behind one card, and the trader chooses which one their
      // account dials. An account sitting on ANY address the CRM lists for a card (`hubUrl`,
      // `hubUrls`, or an advertised gateway endpoint) is on a live gateway of that broker: it is not
      // broken, whatever the shape heuristics above say, and re-homing it would silently undo the
      // trader's choice by moving it to whichever address the CRM reported first.
      if (isKnownGatewayAddress(advertised, account.hubUrl)) continue;

      // Order matters. The stored slug is the LAST resort, never the first: a deep-link that baked
      // an unreachable hub was minted by a misconfigured CRM, and the very same misconfiguration
      // drifts the slug — trusting it re-homes the account onto whichever gateway the bad config
      // named (observed live: a "Vybe Trader" account matched sig-viisync by its stale slug and was
      // "repaired" onto the wrong gateway). The server NAME is the honest discriminator, since it is
      // what the terminal shows the trader; the endpoint is next, and only then the slug.
      const match =
        gateways.find((g) =>
          g.servers.some((s) => s.name === account.serverName)
        ) ??
        gateways.find(
          (g) => g.slug === RETIRED_SERVER_HOMES[account.serverName ?? ""]
        ) ??
        gateways.find((g) =>
          g.servers.some(
            (s) => s.host === account.host && s.port === account.port
          )
        ) ??
        gateways.find((g) => g.slug === account.gatewaySlug);
      if (!match) continue;

      // Re-read: the account may have been edited or removed while we fetched.
      const current = useAccountsStore
        .getState()
        .accounts.find((a) => a.id === account.id);
      if (!current) continue;

      useJournalStore.getState().addEntry({
        level: "warning",
        category: "account",
        message: `Repaired unreachable gateway URL for account ${current.credentials?.login ?? current.id}`,
        detail: {
          from: current.hubUrl,
          to: match.hubUrl,
          slugFrom: current.gatewaySlug,
          slugTo: match.slug,
        },
      });

      useAccountsStore.getState().updateAccount({
        ...current,
        hubUrl: match.hubUrl,
        gatewaySlug: match.slug,
      });
    }
  } catch (error) {
    console.error("[Accounts] Hub URL repair failed:", error);
  }
};

/**
 * One-shot lazy repair for accounts persisted without a broker logo — e.g. added
 * by the agent DSL's advertised-server fast path, or by the CRM "Open in
 * terminal" connect link (which stores the raw host as the server name and may
 * carry no gateway slug at all). Tries the account's own gateway hub first,
 * then every discovered gateway, matching by server name and then by endpoint
 * host:port. Best-effort per account; failures leave the account untouched.
 */
let logoBackfillRunning = false;
export const backfillBrokerLogos = async () => {
  if (logoBackfillRunning) return;
  logoBackfillRunning = true;
  try {
    await runLogoBackfill();
  } finally {
    logoBackfillRunning = false;
  }
};

const runLogoBackfill = async () => {
  // Self-heal, no network: a row that stored the Vybe gateway's REMOTE brand url (the form the
  // catalog advertised whenever a public base url was configured) gets the bundled data-URI, and a
  // Vybe-gateway row with no logo at all gets it too — the tile must paint offline and on a cold
  // PWA start, and this is what repairs devices that already hold the remote form.
  for (const a of useAccountsStore.getState().accounts) {
    const healed = a.brokerLogoUrl
      ? normalizeBrokerLogoUrl(a.brokerLogoUrl)
      : isVybeGatewaySlug(a.gatewaySlug) || a.providerKind === "Vybe"
        ? VYBE_BRAND_LOGO_DATA_URI
        : undefined;
    if (healed && healed !== a.brokerLogoUrl) {
      useAccountsStore.getState().updateAccount({ ...a, brokerLogoUrl: healed });
    }
  }

  const { accounts } = useAccountsStore.getState();
  const candidates = accounts.filter(
    (a) =>
      !a.brokerLogoUrl &&
      !a.gatewaySlug?.startsWith("custom:") &&
      // Gateway-era accounts only: legacy broker-store accounts (no endpoint,
      // no gateway) resolve their connection AND logo by companyName — leave them.
      Boolean(a.gatewaySlug || a.hubUrl || a.host)
  );
  if (candidates.length === 0) return;

  const { resolveCompanyForServer, resolveCompanyForEndpoint } = await import(
    "@/lib/gateways/companies"
  );
  // Same boot race as the hub repair: never skip discovery just because it is mid-flight.
  const knownHubs = (await ensureGatewaysLoaded()).map((g) => g.hubUrl);

  for (const account of candidates) {
    // The account's own hub (when stored) is the most likely home; fall back to
    // every discovered gateway (dedup preserves first occurrence).
    const hubs = Array.from(
      new Set([...(account.hubUrl ? [account.hubUrl] : []), ...knownHubs])
    );

    let resolved: { companyName: string; serverName?: string; logoUrl?: string } | null = null;
    for (const hub of hubs) {
      resolved =
        (await resolveCompanyForServer(hub, account.serverName)) ??
        (account.host
          ? await resolveCompanyForEndpoint(hub, account.host, account.port)
          : null);
      if (resolved) break;
    }
    if (!resolved || !resolved.logoUrl) continue;

    // Re-read: the account may have been edited/deleted while we fetched.
    const current = useAccountsStore
      .getState()
      .accounts.find((a) => a.id === account.id);
    if (!current || current.brokerLogoUrl) continue;
    useAccountsStore.getState().updateAccount({
      ...current,
      companyName: resolved.companyName,
      serverName: resolved.serverName ?? current.serverName,
      brokerLogoUrl: normalizeBrokerLogoUrl(resolved.logoUrl),
    });
  }
};

/**
 * Hook to initialize accounts store
 * Use this in your app root component
 */
export const useInitializeAccounts = () => {
  useEffect(() => {
    initializeAccountsStore();
  }, []);
};

/**
 * Hook to handle account connection and reconnection
 * Manages the entire connection lifecycle
 */
export const useAccountConnection = () => {
  const { client } = useVybeTraderClient();
  const activeAccountId = useAccountsStore((state) => state.activeAccountId);
  // Re-dial requests for the SAME account (picker re-tap, Settings Reconnect, wake watchdog with
  // no session to recycle) — see AccountsStore.reconnectNonce.
  const reconnectNonce = useAccountsStore((state) => state.reconnectNonce);
  const updateAccount = useAccountsStore((state) => state.updateAccount);
  const autoRetryAttemptRef = useRef(0);
  const autoRetryTimerRef = useRef<number | null>(null);

  const cancelAutoRetry = () => {
    if (autoRetryTimerRef.current !== null) {
      window.clearTimeout(autoRetryTimerRef.current);
      autoRetryTimerRef.current = null;
    }
  };

  /** Returns true when a retry was scheduled; false once the schedule is exhausted. */
  const scheduleAutoRetry = (reason: string): boolean => {
    const attempt = autoRetryAttemptRef.current;
    if (attempt >= AUTO_RECONNECT_DELAYS_MS.length) return false;
    autoRetryAttemptRef.current = attempt + 1;
    const delay = AUTO_RECONNECT_DELAYS_MS[attempt];
    useJournalStore.getState().addEntry({
      level: "info",
      category: "connection",
      message: `Connect failed (${reason}) — retrying automatically in ${Math.round(delay / 1000)}s (attempt ${attempt + 1}/${AUTO_RECONNECT_DELAYS_MS.length})`,
      detail: { reason, delayMs: delay, attempt: attempt + 1 },
      sanitizeDetail: false,
    });
    cancelAutoRetry();
    autoRetryTimerRef.current = window.setTimeout(() => {
      autoRetryTimerRef.current = null;
      const { activeAccountId: stillActive, requestReconnect } = useAccountsStore.getState();
      if (!stillActive) return;
      if (useConnectionProgressStore.getState().isConnected) return;
      requestReconnect();
    }, delay);
    return true;
  };
  const getServerHostPort = useBrokersStore((state) => state.getServerHostPort);
  const resetTradeHistory = useTradeHistoryStore((state) => state.reset);
  const resetSymbols = useSymbolsStore((state) => state.reset);
  const isConnecting = useRef(false);

  // Track current account key to detect changes
  const currentAccountKeyRef = useRef<string | null>(null);

  // The account id whose credential we already re-minted this session. Keyed on the ID, not the account
  // key — the key contains the password, so a refreshed credential produces a new key and would let the
  // recovery fire again forever against an account whose password genuinely doesn't work.
  const credentialRefreshedForRef = useRef<string | null>(null);

  // Connection state for error handling
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [hasTriedAllServers, setHasTriedAllServers] = useState(false);
  const [errorMessageToShow, setErrorMessageToShow] = useState<string | null>(
    null
  );

  const handleAccountConnection = async () => {
    // Get current account details
    const { accounts } = useAccountsStore.getState();
    const newAccountKey = generateAccountKey(activeAccountId, accounts);
    const previousAccountKey = currentAccountKeyRef.current;

    if (previousAccountKey !== newAccountKey) {
      useMailboxStore.getState().persistActiveAccount();
      const activeAccount = accounts.find((a) => a.id === activeAccountId);
      const tradingKey = activeAccount ? formatTradingAccountKey(activeAccount) : null;
      if (tradingKey) {
        useMailboxStore.getState().setActiveAccount(tradingKey);
      } else {
        useMailboxStore.getState().setActiveAccount(null);
      }
    }

    // SAFETY: Check connection status and force disconnect if not DISCONNECTED
    const connectionStatus = client.getConnectionStatus();

    if (connectionStatus !== "disconnected") {
      try {
        resetTradeHistory();
        resetSymbols();

        // Force disconnect regardless of current state
        await client.disconnect();

        // Wait for disconnect to complete
        await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (error) {
        console.error(
          "[useAccountConnection] Error during force disconnect:",
          error
        );
      }
    }

    // Update the tracked account key. A different account restarts the automatic-retry budget.
    if (previousAccountKey !== newAccountKey) autoRetryAttemptRef.current = 0;
    currentAccountKeyRef.current = newAccountKey;

    try {
      isConnecting.current = true;
      setHasTriedAllServers(false);
      setShowErrorModal(false);
      setErrorMessageToShow(null);

      // Get active account from store
      const { accounts } = useAccountsStore.getState();
      let activeAccount = activeAccountId
        ? accounts.find((acc) => acc.id === activeAccountId)
        : null;

      if (!activeAccount) {
        return;
      }

      // The boot-time repair runs concurrently with this effect, and the effect is keyed on
      // activeAccountId — a repair that lands mid-attempt never retriggers it. So when the
      // stored hub can't work from here (or was never set), wait for the repair and re-read
      // the row before dialling; otherwise the first boot connects to the stale hub and hangs.
      if (!activeAccount.hubUrl || isHubUrlUnreachable(activeAccount.hubUrl)) {
        await repairUnreachableHubUrls();
        activeAccount =
          useAccountsStore.getState().accounts.find((acc) => acc.id === activeAccountId) ??
          activeAccount;
      }

      // A connect with a blank password can never succeed (the gateway rejects it) and would pop a false
      // "Connection Issue" modal. This happens for accounts opened from a CRM password-less deep-link (the
      // CRM can't read an existing account's password). Skip the doomed attempt — the user enters the
      // password in the account manager (Edit Account → Password), which then reconnects.
      // Exception (single-connection auth, MT5 parity): a Vybe account with no password but a live
      // CRM session connects with X-Vybe-Token — the gateway resolves the credential server-side.
      const sessionAuthed =
        (activeAccount.providerKind ?? "T5") === "Vybe" && !!getVybeToken();

      if (!activeAccount.credentials.password && !sessionAuthed) {
        useJournalStore.getState().addEntry({
          level: "info",
          category: "account",
          message: `Account ${activeAccount.credentials.login} has no saved password — waiting for the user to enter it before connecting`,
          detail: { login: activeAccount.credentials.login },
          sanitizeDetail: false,
        });
        return;
      }

      const providerKind = activeAccount.providerKind ?? "T5";
      // Gateway-bound accounts normally don't choose a provider client-side: the terminal
      // omits X-Trading-Provider and the gateway applies its own pinned default
      // (Gateway__Protocol in VybeAuthenticationMiddleware). Exception: Vybe accounts are
      // ViiSync-engine accounts by definition, so the provider is ALWAYS pinned — if the
      // account's hubUrl ever points at the MT5-pinned gateway (stale storage, desktop's
      // baked default hub), the gateway would otherwise attempt an MT5 login against the
      // ViiSync engine and hang at SendLogin forever with no error.
      const providerEnum =
        providerKind === "Vybe"
          ? TradingProvider.Vybe
          : activeAccount.gatewaySlug
          ? undefined
          : TradingProvider.MT5;

      // Gateway-selected accounts (MT5 and ViiSync alike) carry the exact broker-server endpoint the
      // user picked — always use it. Only fall back to the legacy paths for accounts added before the
      // gateway flow: Vybe → gateway-managed ({"",0}); T5 → the brokers store.
      const servers = activeAccount.host
        ? [{ host: activeAccount.host, port: activeAccount.port ?? 443 }]
        : providerKind === "Vybe"
        ? [{ host: "", port: 0 }]
        : getServerHostPort(
            activeAccount.companyName,
            activeAccount.serverName,
            providerKind
          );

      if (!servers || servers.length === 0) {
        console.error("[useAccountConnection] No servers found for account:", {
          company: activeAccount.companyName,
          server: activeAccount.serverName,
          providerKind,
        });
        return;
      }

      // Create ordered list of servers to try
      let serversToTry = [...servers];

      if (
        providerKind !== "Vybe" &&
        activeAccount.lastConnectedHost &&
        activeAccount.lastConnectedPort
      ) {
        // Remove the last connected server from the list if it exists
        serversToTry = serversToTry.filter(
          (s) =>
            !(
              s.host === activeAccount.lastConnectedHost &&
              s.port === activeAccount.lastConnectedPort
            )
        );

        // Add it at the beginning
        serversToTry.unshift({
          host: activeAccount.lastConnectedHost,
          port: activeAccount.lastConnectedPort,
        });
      }

      // Try to connect to each server until one succeeds
      for (const server of serversToTry) {
        try {
          const credentials = {
            login: parseInt(activeAccount.credentials.login, 10),
            password: activeAccount.credentials.password,
            host: server.host,
            port: server.port,
            provider: providerEnum,
            // Route to this account's gateway SignalR host (undefined → default hub).
            hubUrl: activeAccount.hubUrl,
          };

          useJournalStore.getState().addEntry({
            level: "info",
            category: "account",
            message: `Connecting account ${credentials.login} @ ${server.host}:${server.port} (${providerKind})`,
            detail: {
              login: credentials.login,
              host: server.host,
              port: server.port,
              providerKind,
            },
            sanitizeDetail: false,
          });

          // Reset connection progress before each attempt
          const progressStore = useConnectionProgressStore.getState();
          progressStore.reset();

          // Start connection attempt
          await client.connect(credentials);

          // Wait a moment for connection to start
          await new Promise((resolve) => setTimeout(resolve, 50));

          // Wait for connection to complete using isConnecting status. The deadline is the
          // only exit for a gateway that accepts the socket and then never answers the login
          // (stale hub, wrong provider pin) — without it the loop spins forever and the user
          // sees "nothing happened" instead of a failed attempt.
          const connectDeadline = Date.now() + 45_000;
          while (true) {
            const currentState = useConnectionProgressStore.getState();

            if (Date.now() > connectDeadline && !currentState.isConnected) {
              useJournalStore.getState().addEntry({
                level: "warning",
                category: "connection",
                message: `Connection to ${server.host}:${server.port} timed out after 45s — trying the next server`,
                detail: { host: server.host, port: server.port, hubUrl: activeAccount.hubUrl ?? null },
                sanitizeDetail: false,
              });
              try {
                await client.disconnect();
              } catch {
                // best effort — the socket may already be gone
              }
              break;
            }

            // If connected successfully, break out of both loops
            if (currentState.isConnected) {
              // Update account with last connected server info
              const updatedAccount = {
                ...activeAccount,
                lastConnectedHost: server.host,
                lastConnectedPort: server.port,
                lastConnectedAt: Date.now(),
              };
              updateAccount(updatedAccount);

              try {
                await prepareAndSyncMailbox(client, updatedAccount);
              } catch (mailboxError) {
                console.error("[useAccountConnection] Mailbox sync failed:", mailboxError);
              }

              try {
                await prepareAndSyncNews(client);
              } catch (newsError) {
                console.error("[useAccountConnection] News sync failed:", newsError);
              }

              // This credential works, so forget any refresh we did for it: should it be rotated
              // again later, that next failure deserves its own recovery attempt.
              credentialRefreshedForRef.current = null;
              autoRetryAttemptRef.current = 0;

              playNotificationSound("accountConnected");
              return; // Exit the entire function
            }

            // If error occurred, check if it's a critical error
            if (currentState.hasIssues) {
              const errorMessage = currentState.lastMessage?.message || "";

              // Check for critical errors that should stop all connection attempts
              if (isCriticalConnectionError(errorMessage)) {
                console.error(
                  `[useAccountConnection] Critical error - ${errorMessage}. Stopping all connection attempts.`
                );
                showConnectionError(
                  errorMessage,
                  setErrorMessageToShow,
                  setShowErrorModal,
                  setHasTriedAllServers
                );
                playNotificationSound("accountConnectFailed");
                return;
              }

              // Regular connection error - try next server
              console.error(
                `[useAccountConnection] Connection failed for ${server.host}:${server.port}: ${errorMessage}`
              );
              break;
            }

            // If no longer connecting, check if we should break
            if (
              !currentState.isConnecting &&
              currentState.messageHistory.length > 0
            ) {
              const stalledMessage = currentState.lastMessage?.message || "";
              if (isCriticalConnectionError(stalledMessage)) {
                showConnectionError(
                  stalledMessage,
                  setErrorMessageToShow,
                  setShowErrorModal,
                  setHasTriedAllServers
                );
                playNotificationSound("accountConnectFailed");
                return;
              }

              console.error(
                `[useAccountConnection] Connection stopped for ${server.host}:${server.port} without success or error`
              );
              break;
            }

            // Still connecting, wait before checking again
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        } catch (error) {
          if (isHubConnectionClosedError(error)) {
            return;
          }
          console.error(
            `[useAccountConnection] Failed to connect to ${server.host}:${server.port}:`,
            error
          );
          // Continue to next server
        }
      }

      // If we've tried all servers and none worked
      const finalState = useConnectionProgressStore.getState();
      if (!finalState.isConnected) {
        console.error("[useAccountConnection] Failed to connect to any server");
        const message =
          finalState.lastMessage?.type === 2
            ? finalState.lastMessage.message
            : "Connection failed.";

        // A Vybe account whose saved password the gateway rejected may simply have been rotated
        // server-side (any CRM mint does that — another device, or a portal reset). Mint a fresh
        // credential and try once more before telling the user their account is broken. Once per
        // account per session: if the new credential is rejected too, the fault isn't staleness.
        if (
          providerKind === "Vybe" &&
          isAuthFailure(message) &&
          activeAccountId &&
          credentialRefreshedForRef.current !== activeAccountId
        ) {
          credentialRefreshedForRef.current = activeAccountId;
          const login = parseInt(activeAccount.credentials.login, 10);
          useJournalStore.getState().addEntry({
            level: "info",
            category: "account",
            message: `Account ${login} was rejected — refreshing its credential and retrying once`,
            detail: { login },
            sanitizeDetail: false,
          });

          if (await refreshVybeCredential(login)) {
            isConnecting.current = false;
            await handleAccountConnection();
            return;
          }
        }

        // Transient (not a rejected credential, not a broker-side verdict): re-dial on a schedule
        // instead of stopping at a modal the user has to relaunch the app to get past.
        if (
          !isAuthFailure(message) &&
          !isCriticalConnectionError(message) &&
          scheduleAutoRetry(message)
        ) {
          return;
        }

        showConnectionError(
          message,
          setErrorMessageToShow,
          setShowErrorModal,
          setHasTriedAllServers
        );
        playNotificationSound("accountConnectFailed");
      }
    } catch (error) {
      console.error("[useAccountConnection] Connection error:", error);
    } finally {
      isConnecting.current = false;
    }
  };

  useEffect(() => {
    // A new run supersedes any pending automatic retry; an explicit account switch also restarts
    // the retry budget (a different gateway deserves its own chances).
    cancelAutoRetry();
    void handleAccountConnection().catch((error) => {
      console.error("[useAccountConnection] Unhandled connection error:", error);
    });
  }, [
    activeAccountId,
    reconnectNonce,
    client,
    getServerHostPort,
    resetTradeHistory,
    resetSymbols,
    updateAccount,
  ]);

  return {
    showErrorModal,
    errorMessageToShow,
    hasTriedAllServers,
    setShowErrorModal,
    setErrorMessageToShow,
  };
};
